import {
  Button,
  ConfigProvider,
  Flex,
  Form,
  Input,
  Modal,
  Select,
  Typography,
  message,
} from "antd";
import React, { forwardRef, useImperativeHandle, useState } from "react";
import {
  EditOutlined,
  HomeOutlined,
  CreditCardOutlined,
} from "@ant-design/icons";
import http from "@/utils/http";
import api from "@/utils/api";

/* ================= 样式 & 常量(放在组件外面) ================= */
const sectionCardStyle = {
  flex: 1,
  minWidth: 300,
  border: "1px solid #e5e7eb",
  borderRadius: 6,
  background: "#fff",
  boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
  overflow: "hidden",
  alignSelf: "flex-start",
};

const sectionHeaderStyle = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "10px 20px",
  background: "#f7f8fa",
  borderBottom: "1px solid #e5e7eb",
  fontSize: 18,
};

const sectionBodyStyle = { padding: "16px 20px 0" };

const SectionCard = ({ icon, title, children, style }) => (
  <div style={{ ...sectionCardStyle, ...style }}>
    <div style={sectionHeaderStyle}>
      {icon}
      <Typography.Text strong style={{ fontSize: 18, fontWeight: 700 }}>
        {title}
      </Typography.Text>
    </div>
    <div style={sectionBodyStyle}>{children}</div>
  </div>
);

const compactTheme = {
  components: {
    Form: { itemMarginBottom: 12 },
  },
};

const filterOption = (input, option) =>
  (option?.label ?? "").toLowerCase().includes(input.toLowerCase());

const searchProps = {
  allowClear: true,
  showSearch: true,
  optionFilterProp: "label",
  filterOption,
};

/* ================= 懒加载下拉 hook ================= */
function useLazySelect(apiUrl, key, mapper, enableCache = true) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const load = async () => {
    if (enableCache && loaded) return;
    setLoading(true);
    try {
      const res = await http.get(apiUrl);
      if (res?.code === "1") {
        const list = res?.data?.[key] || res?.data || [];
        setOptions(mapper(list));
        setLoaded(true);
      } else {
        setOptions([]);
      }
    } catch (err) {
      console.error("Select load error:", err);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  };

  return { options, loading, load };
}

function removeEmptyFields(data) {
  return Object.fromEntries(
    Object.entries(data).filter(([, value]) => {
      if (value === undefined || value === null) return false;
      if (typeof value === "string" && value.trim() === "") return false;
      if (Array.isArray(value) && value.length === 0) return false;
      return true;
    }),
  );
}

const InfoUpdateModal = forwardRef((props, ref) => {
  const { onSuccess } = props;
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);

  const propTypeSelect = useLazySelect(api.getPropTypeList, null, (data) =>
    (data || []).map((item) => ({
      label: item?.name ?? item?.code ?? String(item?.id ?? ""),
      value: item?.name ?? item?.code ?? String(item?.id ?? ""),
    })),
  );

  const trustSelect = useLazySelect(api.getTrustDictList, "trust", (data) =>
    (data || []).map((item) => ({
      label: item?.trustNameTitle ?? item?.trustName ?? item?.label ?? "",
      value: String(item?.trustId ?? item?.value ?? ""),
    })),
  );

  const companySelect = useLazySelect(api.getOwnerDictList, "company", (data) =>
    (data || []).map((item) => ({
      label: item?.titleName,
      value: item?.ownerId,
    })),
  );

  const groupSelect = useLazySelect(api.commonGroupList, "group", (data) =>
    (data || []).map((item) => ({
      label: item?.geographyGroup,
      value: item?.groupId,
    })),
  );

  const citySelect = useLazySelect(api.getPropCityDict, "city", (data) =>
    (data || []).map((item) => ({
      label: item?.label ?? "",
      value: String(item?.value ?? ""),
    })),
  );

  useImperativeHandle(ref, () => ({
    setModelAdd: async () => {
      form.resetFields();
      setVisible(true);
      await Promise.all([
        propTypeSelect.load(),
        trustSelect.load(),
        companySelect.load(),
        groupSelect.load(),
        citySelect.load(),
      ]);
    },
  }));

  const onFinish = async (values) => {
    setLoading(true);
    try {
      const propsList = values?.props
        ? values.props
            .split(/[\n,]/)
            .map((item) => item.trim())
            .filter(Boolean)
        : [];

      const payload = removeEmptyFields({
        props: propsList,
        propType: values?.propType,
        groupId: values?.groupId,
        trustId: values?.trustId,
        city: values?.city,
        companyId: values?.companyId,
        manFlatAmt: values?.manFlatAmt,
        manPct: values?.manPct,
        toGroupId: values?.toGroupId,
      });

      const res = await http.post(`${api.getPropInfoUpdate}`, payload);
      if (res?.code === "1") {
        message.success("Property Updated Successfully");
        setVisible(false);
        form.resetFields();
        onSuccess && onSuccess();
      } else {
        message.error(res?.msg || "Update failed");
      }
    } catch (err) {
      console.error(err);
      message.error(err?.msg || "Request failed, please try again");
    } finally {
      setLoading(false);
    }
  };

  const closeModal = () => {
    setVisible(false);
    form.resetFields();
  };

  return (
    <ConfigProvider theme={compactTheme}>
      <Modal
        title={
          <Flex justify="flex-start" align="flex-start" className="model_title">
            <EditOutlined className="model_title_mr10" />
            <Typography.Title
              level={4}
              className="mg"
              style={{ fontWeight: 700 }}
            >
              PROP. INFO UPDATE
            </Typography.Title>
          </Flex>
        }
        width="60%"
        open={visible}
        maskClosable={false}
        destroyOnClose
        onCancel={closeModal}
        footer={[
          <Flex key="footer" justify="center" gap={12}>
            <Button onClick={closeModal}>Close</Button>
            <Button
              type="primary"
              loading={loading}
              onClick={() => form.submit()}
            >
              Submit
            </Button>
          </Flex>,
        ]}
      >
        <div style={{ width: "100%" }}>
          <Form
            form={form}
            name="updatePropertyForm"
            layout="horizontal"
            labelCol={{ style: { width: "150px" } }}
            wrapperCol={{ style: { flex: 1 } }}
            onFinish={onFinish}
          >
            <Flex align="flex-start" justify="space-between" gap={24}>
              {/* ================= 1. Filters ================= */}
              <SectionCard icon={<HomeOutlined />} title="Filters">
                <Form.Item
                  name="props"
                  label="Property"
                  rules={[{ required: true, message: "Please enter Property" }]}
                >
                  <Input.TextArea placeholder="Property" allowClear />
                </Form.Item>

                <Form.Item name="propType" label="Property Type">
                  <Select
                    placeholder="Select Property Type"
                    loading={propTypeSelect.loading}
                    options={propTypeSelect.options}
                    {...searchProps}
                  />
                </Form.Item>

                <Form.Item name="city" label="City">
                  <Select
                    placeholder="Select City"
                    loading={citySelect.loading}
                    options={citySelect.options}
                    {...searchProps}
                  />
                </Form.Item>

                <Form.Item name="groupId" label="Group">
                  <Select
                    placeholder="Select Group"
                    loading={groupSelect.loading}
                    options={groupSelect.options}
                    {...searchProps}
                  />
                </Form.Item>

                <Form.Item name="trustId" label="Trust">
                  <Select
                    placeholder="Select Trust"
                    loading={trustSelect.loading}
                    options={trustSelect.options}
                    {...searchProps}
                  />
                </Form.Item>

                <Form.Item name="companyId" label="Management Company">
                  <Select
                    placeholder="Select Company"
                    loading={companySelect.loading}
                    options={companySelect.options}
                    {...searchProps}
                  />
                </Form.Item>
              </SectionCard>

              {/* ================= 2. To Update Data ================= */}
              <SectionCard icon={<CreditCardOutlined />} title="To Update Data">
                <Form.Item name="manFlatAmt" label="Mgt Fee Flat">
                  <Input placeholder="Purchase Mgt Fee Flat" allowClear />
                </Form.Item>

                <Form.Item name="manPct" label="Mgt Fee %">
                  <Input placeholder="Mgt Fee %" allowClear />
                </Form.Item>

                <Form.Item name="toGroupId" label="Group">
                  <Select
                    placeholder="Select Group"
                    loading={groupSelect.loading}
                    options={groupSelect.options}
                    {...searchProps}
                  />
                </Form.Item>
              </SectionCard>
            </Flex>
          </Form>
        </div>
      </Modal>
    </ConfigProvider>
  );
});

export default InfoUpdateModal;